import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AppLayout from 'layouts/AppLayout';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import { MOCK_FUNDS, MOCK_SPVS } from 'data/mockData';
import { getFundMetrics, getGPAllocation } from 'data/fundUtils';
import { ROUTES } from 'config/navConfig';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt(n) {
  if (n == null) return '—';
  if (n >= 1_000_000) return `£${(n / 1_000_000).toFixed(0)}m`;
  if (n >= 1_000)     return `£${(n / 1_000).toFixed(0)}k`;
  return `£${n}`;
}

function fmtPct(n, dp = 1) {
  return n != null ? `${n.toFixed(dp)}%` : '—';
}

function fmtX(n) {
  return n != null ? `${n.toFixed(2)}x` : '—';
}

function StatusBadge({ status }) {
  const cfg = status === 'Closed'
    ? { bg: 'var(--color-success-subtle)', color: 'var(--color-success)', label: 'Closed' }
    : { bg: 'var(--color-warning-subtle)', color: 'var(--color-warning)', label: 'Raising' };
  return (
    <span
      className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium font-caption"
      style={{ background: cfg.bg, color: cfg.color }}
    >
      {cfg.label}
    </span>
  );
}

function LPStatusBadge({ status }) {
  const cfg = {
    closed:       { bg: 'var(--color-success-subtle)',  color: 'var(--color-success)',  label: 'Closed' },
    'soft-circled': { bg: 'var(--color-warning-subtle)', color: 'var(--color-warning)', label: 'Soft-Circled' },
    defaulted:    { bg: 'var(--color-destructive-subtle)', color: 'var(--color-destructive)', label: 'Defaulted' },
  }[status] ?? { bg: 'var(--color-muted)', color: 'var(--color-muted-foreground)', label: status };
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium font-caption"
      style={{ background: cfg.bg, color: cfg.color }}
    >
      {cfg.label}
    </span>
  );
}

// ─── Tab: Overview ────────────────────────────────────────────────────────────

function OverviewTab({ fund, m }) {
  const navigate = useNavigate();
  const isFund2  = fund.id === 'fund-2';

  const structureRows = [
    ['Fund Manager',       fund.fundManager],
    ['Fund Term',          `${fund.fundTerm} years`],
    ['Investment Period',  `${fund.investmentPeriod} years`],
    ['Domicile',           fund.domicile],
    ['Regulatory Status',  fund.regulatoryStatus],
    ['Currency',           fund.currency],
    ['Management Fee',     fmtPct(fund.managementFee) + ' p.a.'],
    ['Carried Interest',   fmtPct(fund.carry)],
    ['Preferred Return',   fmtPct(fund.preferredReturn) + ' hurdle'],
    ['Inception Date',     fund.inceptionDate],
    ['First Close',        fund.firstCloseDate ?? '—'],
    ['Final Close',        fund.finalCloseDate ?? '—'],
  ];

  return (
    <div className="space-y-6">
      {/* Fund II pipeline alert */}
      {isFund2 && (
        <div
          className="flex items-start gap-3 px-4 py-3 rounded-xl border"
          style={{ background: 'var(--color-warning-subtle)', borderColor: 'var(--color-warning)' }}
        >
          <Icon name="AlertCircle" size={16} color="var(--color-warning)" className="mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium font-caption" style={{ color: 'var(--color-warning)' }}>
              1 deal at IC Review — pending deployment decision
            </p>
            <p className="text-xs font-caption mt-0.5" style={{ color: 'var(--color-muted-foreground)' }}>
              Avon Water Infrastructure is awaiting IC approval before SPV formation. View pipeline to track progress.
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(ROUTES.pipeline)}
          >
            View Pipeline
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Metrics column */}
        <div className="lg:col-span-2 space-y-4">
          {/* Capital metrics */}
          <div className="rounded-xl border border-border p-5" style={{ background: 'var(--color-card)' }}>
            <h3 className="font-heading font-semibold text-foreground text-sm mb-4">Capital Account</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {[
                { label: 'Target Size',        value: fmt(fund.targetSize) },
                { label: fund.status === 'Raising' ? 'Soft-Circled' : 'Total Committed', value: fund.status === 'Raising' ? fmt(fund.raisedSoftCircled) : m.fmtCommitted },
                { label: 'Capital Called',     value: m.fmtCalled,       sub: `${m.calledPct}% called` },
                { label: 'Distributed',        value: m.fmtDistributed },
                { label: 'Deployed to SPVs',   value: m.fmtDeployed },
                { label: 'Undeployed Capital', value: m.fmtUndeployed },
              ].map((tile) => (
                <div key={tile.label} className="flex flex-col gap-0.5">
                  <span className="text-xs font-caption" style={{ color: 'var(--color-muted-foreground)' }}>{tile.label}</span>
                  <span className="text-lg font-semibold font-heading" style={{ color: 'var(--color-foreground)' }}>{tile.value}</span>
                  {tile.sub && <span className="text-xs font-caption" style={{ color: 'var(--color-muted-foreground)' }}>{tile.sub}</span>}
                </div>
              ))}
            </div>
          </div>

          {/* Performance (Fund I only) */}
          {fund.tvpiMultiple != null ? (
            <div className="rounded-xl border border-border p-5" style={{ background: 'var(--color-card)' }}>
              <h3 className="font-heading font-semibold text-foreground text-sm mb-4">Performance</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {[
                  { label: 'Net IRR',    value: fmtPct(fund.netIRR) },
                  { label: 'Gross IRR',  value: fmtPct(fund.grossIRR) },
                  { label: 'TVPI',       value: fmtX(fund.tvpiMultiple) },
                  { label: 'DPI',        value: fmtX(fund.dpiMultiple) },
                  { label: 'RVPI',       value: fmtX(fund.rvpiMultiple) },
                  { label: 'NAV',        value: fmt(fund.nav) },
                ].map((tile) => (
                  <div key={tile.label} className="flex flex-col gap-0.5">
                    <span className="text-xs font-caption" style={{ color: 'var(--color-muted-foreground)' }}>{tile.label}</span>
                    <span className="text-lg font-semibold font-heading" style={{ color: 'var(--color-foreground)' }}>{tile.value}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div
              className="flex items-center gap-3 rounded-xl border border-dashed border-border p-5"
              style={{ background: 'var(--color-card)' }}
            >
              <Icon name="BarChart2" size={20} color="var(--color-muted-foreground)" />
              <div>
                <p className="text-sm font-medium font-caption text-foreground">Performance Pending</p>
                <p className="text-xs font-caption mt-0.5" style={{ color: 'var(--color-muted-foreground)' }}>
                  Performance data will appear once capital is deployed into SPVs.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Fund structure */}
        <div className="rounded-xl border border-border p-5 h-fit" style={{ background: 'var(--color-card)' }}>
          <h3 className="font-heading font-semibold text-foreground text-sm mb-4">Fund Structure</h3>
          <dl className="space-y-3">
            {structureRows.map(([label, value]) => (
              <div key={label} className="flex justify-between gap-2 text-sm">
                <dt className="font-caption shrink-0" style={{ color: 'var(--color-muted-foreground)' }}>{label}</dt>
                <dd className="font-caption font-medium text-right" style={{ color: 'var(--color-foreground)' }}>{value}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </div>
  );
}

// ─── Tab: LP Commitments ──────────────────────────────────────────────────────

function LPCommitmentsTab({ fund, m }) {
  const isFund2 = fund.id === 'fund-2';

  return (
    <div className="space-y-5">
      {isFund2 && (
        <div
          className="flex items-center gap-3 px-4 py-3 rounded-xl border"
          style={{ background: 'var(--color-primary-subtle)', borderColor: 'var(--color-primary)' }}
        >
          <Icon name="Info" size={15} color="var(--color-primary)" className="shrink-0" />
          <p className="text-sm font-caption" style={{ color: 'var(--color-primary)' }}>
            Fund II is currently in raise. LP commitments shown are soft-circled and not yet legally binding.
          </p>
        </div>
      )}

      <div className="rounded-xl border border-border overflow-hidden" style={{ background: 'var(--color-card)' }}>
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <h3 className="font-heading font-semibold text-foreground text-sm">LP Commitments</h3>
          <span className="text-xs font-caption px-2 py-0.5 rounded-full" style={{ background: 'var(--color-muted)', color: 'var(--color-muted-foreground)' }}>
            {m.lpCount} LPs
          </span>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border" style={{ background: 'var(--color-muted)' }}>
                {['LP Name', 'Type', 'Commitment', 'Called', 'Distributed', 'Called %', 'Status'].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-left text-xs font-medium font-caption"
                    style={{ color: 'var(--color-muted-foreground)' }}
                  >{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {fund.lpCommitments.map((lp, i) => {
                const calledPct = lp.commitmentAmount
                  ? ((lp.calledAmount / lp.commitmentAmount) * 100).toFixed(1)
                  : '0.0';
                const isGP = lp.type === 'GP';
                return (
                  <tr
                    key={lp.id}
                    className="border-b border-border last:border-0 transition-colors hover:bg-muted/30"
                    style={isGP ? { background: 'var(--color-primary-subtle)' } : {}}
                  >
                    <td className="px-4 py-3 font-medium font-caption" style={{ color: 'var(--color-foreground)' }}>
                      <div className="flex items-center gap-2">
                        {isGP && <Icon name="Building2" size={12} color="var(--color-primary)" />}
                        {lp.name}
                        {isGP && <span className="text-xs font-caption px-1.5 py-0.5 rounded" style={{ background: 'var(--color-primary)', color: '#fff', fontSize: '0.6rem' }}>GP</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-caption" style={{ color: 'var(--color-muted-foreground)' }}>{lp.type}</td>
                    <td className="px-4 py-3 font-data font-medium" style={{ color: 'var(--color-foreground)' }}>{fmt(lp.commitmentAmount)}</td>
                    <td className="px-4 py-3 font-data" style={{ color: 'var(--color-foreground)' }}>{fmt(lp.calledAmount)}</td>
                    <td className="px-4 py-3 font-data" style={{ color: 'var(--color-foreground)' }}>{fmt(lp.distributedAmount)}</td>
                    <td className="px-4 py-3 font-data" style={{ color: 'var(--color-foreground)' }}>
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--color-border)' }}>
                          <div
                            className="h-full rounded-full"
                            style={{ width: `${Math.min(parseFloat(calledPct), 100)}%`, background: 'var(--color-primary)' }}
                          />
                        </div>
                        <span className="text-xs">{calledPct}%</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <LPStatusBadge status={lp.status} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {/* Summary footer */}
            <tfoot>
              <tr style={{ background: 'var(--color-muted)' }}>
                <td className="px-4 py-3 font-medium font-caption text-foreground" colSpan={2}>Total</td>
                <td className="px-4 py-3 font-data font-semibold text-foreground">{m.fmtCommitted}</td>
                <td className="px-4 py-3 font-data font-semibold text-foreground">{m.fmtCalled}</td>
                <td className="px-4 py-3 font-data font-semibold text-foreground">{m.fmtDistributed}</td>
                <td className="px-4 py-3 font-data font-semibold text-foreground">{m.calledPct}%</td>
                <td className="px-4 py-3" />
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── Tab: SPVs & Portfolio ────────────────────────────────────────────────────

function SPVsTab({ fund, m }) {
  const navigate = useNavigate();

  const statusCfg = {
    active:        { color: 'var(--color-success)',   bg: 'var(--color-success-subtle)',  label: 'Active' },
    fundraising:   { color: 'var(--color-primary)',   bg: 'var(--color-primary-subtle)',  label: 'Fundraising' },
    'pre-formation': { color: 'var(--color-warning)', bg: 'var(--color-warning-subtle)',  label: 'Pre-Formation' },
    exited:        { color: 'var(--color-muted-foreground)', bg: 'var(--color-muted)',    label: 'Exited' },
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <p className="text-sm font-caption" style={{ color: 'var(--color-muted-foreground)' }}>
          {m.spvCount} SPV{m.spvCount !== 1 ? 's' : ''} · {m.fmtDeployed} deployed
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {m.fundSPVs.map((spv) => {
          const gpAlloc = getGPAllocation(spv);
          const gpAmt   = gpAlloc?.commitmentAmount ?? spv.gpFundAllocation;
          const sCfg    = statusCfg[spv.status] ?? statusCfg['pre-formation'];
          const pctRaised = spv.targetSize ? ((spv.raisedAmount / spv.targetSize) * 100).toFixed(0) : null;

          return (
            <div
              key={spv.id}
              className="rounded-xl border border-border p-5 cursor-pointer transition-shadow hover:shadow-md flex flex-col gap-4"
              style={{ background: 'var(--color-card)' }}
              onClick={() => navigate(ROUTES.spvDashboard, { state: { spvId: spv.id } })}
            >
              {/* SPV header */}
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-heading font-semibold text-sm text-foreground leading-tight">{spv.name}</p>
                  <p className="text-xs font-caption mt-0.5" style={{ color: 'var(--color-muted-foreground)' }}>
                    {spv.sector} · {spv.jurisdiction}
                  </p>
                </div>
                <span
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium font-caption shrink-0"
                  style={{ background: sCfg.bg, color: sCfg.color }}
                >
                  {sCfg.label}
                </span>
              </div>

              {/* Capital metrics */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs font-caption" style={{ color: 'var(--color-muted-foreground)' }}>GP Allocation</p>
                  <p className="text-sm font-semibold font-data text-foreground">{fmt(gpAmt)}</p>
                  <p className="text-xs font-caption" style={{ color: 'var(--color-muted-foreground)' }}>from {fund.shortName}</p>
                </div>
                <div>
                  <p className="text-xs font-caption" style={{ color: 'var(--color-muted-foreground)' }}>SPV Size</p>
                  <p className="text-sm font-semibold font-data text-foreground">{fmt(spv.raisedAmount)}</p>
                  <p className="text-xs font-caption" style={{ color: 'var(--color-muted-foreground)' }}>of {fmt(spv.targetSize)} target</p>
                </div>
              </div>

              {/* Progress bar */}
              {pctRaised != null && (
                <div>
                  <div className="flex justify-between text-xs font-caption mb-1" style={{ color: 'var(--color-muted-foreground)' }}>
                    <span>Capital raised</span>
                    <span>{pctRaised}%</span>
                  </div>
                  <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--color-border)' }}>
                    <div
                      className="h-full rounded-full"
                      style={{ width: `${Math.min(parseFloat(pctRaised), 100)}%`, background: 'var(--color-primary)' }}
                    />
                  </div>
                </div>
              )}

              {/* IRR & footer */}
              <div className="flex items-center justify-between pt-1 border-t border-border">
                <div className="flex items-center gap-1.5 text-xs font-caption" style={{ color: 'var(--color-muted-foreground)' }}>
                  <Icon name="TrendingUp" size={12} color="var(--color-muted-foreground)" />
                  {spv.targetIRR} target IRR
                </div>
                <div className="flex items-center gap-1 text-xs font-medium font-caption" style={{ color: 'var(--color-primary)' }}>
                  View SPV <Icon name="ArrowRight" size={12} color="var(--color-primary)" />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Tab: Performance ─────────────────────────────────────────────────────────

function PerformanceTab({ fund, m }) {
  if (fund.tvpiMultiple == null) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4 text-center">
        <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ background: 'var(--color-muted)' }}>
          <Icon name="BarChart2" size={24} color="var(--color-muted-foreground)" />
        </div>
        <div>
          <p className="font-heading font-semibold text-foreground">Performance data pending</p>
          <p className="text-sm font-caption mt-1" style={{ color: 'var(--color-muted-foreground)' }}>
            Performance metrics will populate once capital is deployed and first valuations are conducted.
          </p>
        </div>
      </div>
    );
  }

  const metrics = [
    { label: 'Net IRR',    value: fmtPct(fund.netIRR),        icon: 'TrendingUp',      note: 'After management fee & carry' },
    { label: 'Gross IRR',  value: fmtPct(fund.grossIRR),      icon: 'TrendingUp',      note: 'Before fees & carry' },
    { label: 'TVPI',       value: fmtX(fund.tvpiMultiple),    icon: 'Layers',          note: 'Total Value to Paid-In' },
    { label: 'DPI',        value: fmtX(fund.dpiMultiple),     icon: 'ArrowDownCircle', note: 'Distributions to Paid-In' },
    { label: 'RVPI',       value: fmtX(fund.rvpiMultiple),    icon: 'PieChart',        note: 'Residual Value to Paid-In' },
    { label: 'NAV',        value: fmt(fund.nav),              icon: 'Wallet',          note: 'Net Asset Value' },
  ];

  const deployment = [
    { label: 'Total Committed by LPs', value: m.fmtCommitted },
    { label: 'Capital Called',          value: m.fmtCalled,       sub: `${m.calledPct}% called` },
    { label: 'Deployed to SPVs',        value: m.fmtDeployed },
    { label: 'Undeployed Capital',      value: m.fmtUndeployed },
    { label: 'Distributions Paid',      value: m.fmtDistributed },
  ];

  return (
    <div className="space-y-6">
      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {metrics.map((kpi) => (
          <div
            key={kpi.label}
            className="rounded-xl border border-border p-5"
            style={{ background: 'var(--color-card)' }}
          >
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'var(--color-primary-subtle)' }}>
                <Icon name={kpi.icon} size={14} color="var(--color-primary)" />
              </div>
              <span className="text-xs font-caption" style={{ color: 'var(--color-muted-foreground)' }}>{kpi.label}</span>
            </div>
            <p className="text-2xl font-semibold font-heading text-foreground">{kpi.value}</p>
            <p className="text-xs font-caption mt-1" style={{ color: 'var(--color-muted-foreground)' }}>{kpi.note}</p>
          </div>
        ))}
      </div>

      {/* Capital deployment waterfall */}
      <div className="rounded-xl border border-border p-5" style={{ background: 'var(--color-card)' }}>
        <h3 className="font-heading font-semibold text-foreground text-sm mb-4 flex items-center gap-2">
          <Icon name="GitBranch" size={15} color="var(--color-primary)" />
          Capital Deployment Summary
        </h3>
        <div className="space-y-3">
          {deployment.map((row) => (
            <div key={row.label} className="flex items-center justify-between gap-4 py-2 border-b border-border last:border-0">
              <span className="text-sm font-caption" style={{ color: 'var(--color-muted-foreground)' }}>{row.label}</span>
              <div className="text-right">
                <span className="text-sm font-semibold font-data text-foreground">{row.value}</span>
                {row.sub && <span className="block text-xs font-caption" style={{ color: 'var(--color-muted-foreground)' }}>{row.sub}</span>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Hurdle note */}
      <div
        className="flex items-start gap-3 px-4 py-3 rounded-xl border"
        style={{ background: 'var(--color-primary-subtle)', borderColor: 'var(--color-primary)' }}
      >
        <Icon name="Info" size={14} color="var(--color-primary)" className="shrink-0 mt-0.5" />
        <p className="text-xs font-caption" style={{ color: 'var(--color-primary)' }}>
          Preferred return hurdle is {fmtPct(fund.preferredReturn)}. Carried interest of {fmtPct(fund.carry)} applies above the hurdle.
          Performance figures as of most recent quarterly valuation.
        </p>
      </div>
    </div>
  );
}

// ─── Tab: Documents ───────────────────────────────────────────────────────────

function DocumentsTab() {
  return (
    <div className="flex flex-col items-center justify-center py-20 gap-4 text-center">
      <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ background: 'var(--color-muted)' }}>
        <Icon name="FolderOpen" size={24} color="var(--color-muted-foreground)" />
      </div>
      <div>
        <p className="font-heading font-semibold text-foreground">Fund Documents</p>
        <p className="text-sm font-caption mt-1" style={{ color: 'var(--color-muted-foreground)' }}>
          LPA, PPM, and quarterly reports will appear here.
        </p>
      </div>
      <button
        className="text-sm font-medium font-caption"
        style={{ color: 'var(--color-primary)' }}
        onClick={() => {}}
      >
        Browse Data Room
      </button>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

const TABS = [
  { id: 'overview',     label: 'Overview',        icon: 'LayoutDashboard' },
  { id: 'lp',          label: 'LP Commitments',   icon: 'Users' },
  { id: 'spvs',        label: 'SPVs & Portfolio', icon: 'Layers' },
  { id: 'performance', label: 'Performance',      icon: 'TrendingUp' },
  { id: 'documents',   label: 'Documents',        icon: 'FolderOpen' },
];

export default function FundDetailPage() {
  const { id }         = useParams();
  const navigate       = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');

  const fund = MOCK_FUNDS.find((f) => f.id === id);
  const m    = fund ? getFundMetrics(fund, MOCK_SPVS) : null;

  if (!fund) {
    return (
      <AppLayout>
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center space-y-3">
            <Icon name="Landmark" size={32} color="var(--color-muted-foreground)" />
            <p className="font-heading font-semibold text-foreground">Fund not found</p>
            <Button variant="outline" onClick={() => navigate(ROUTES.funds)}>Back to Funds</Button>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="min-h-screen bg-background">

        {/* Sticky header */}
        <div
          className="sticky top-0 bg-background border-b border-border px-6 md:px-10 lg:px-12 py-4"
          style={{ zIndex: 'var(--z-nav)' }}
        >
          {/* Breadcrumb */}
          <div className="flex items-center gap-1.5 text-xs font-caption mb-2" style={{ color: 'var(--color-muted-foreground)' }}>
            <button
              className="hover:underline"
              style={{ color: 'var(--color-muted-foreground)' }}
              onClick={() => navigate(ROUTES.funds)}
            >
              Funds
            </button>
            <Icon name="ChevronRight" size={12} color="var(--color-muted-foreground)" />
            <span style={{ color: 'var(--color-foreground)' }}>{fund.shortName}</span>
          </div>

          <div className="flex items-start justify-between gap-4 flex-wrap pl-10 lg:pl-0">
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                style={{ background: 'var(--color-primary-subtle)' }}
              >
                <Icon name="Landmark" size={18} color="var(--color-primary)" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="font-heading font-semibold text-foreground" style={{ fontSize: '1.2rem' }}>{fund.name}</h1>
                  <StatusBadge status={fund.status} />
                </div>
                <p className="text-xs font-caption mt-0.5" style={{ color: 'var(--color-muted-foreground)' }}>
                  {fund.fundManager} · {m.lpCount} LPs · {m.spvCount} SPVs
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                iconName="ArrowLeft"
                iconPosition="left"
                iconSize={14}
                onClick={() => navigate(ROUTES.funds)}
              >
                All Funds
              </Button>
            </div>
          </div>
        </div>

        {/* Tab nav */}
        <div
          className="border-b border-border px-6 md:px-10 lg:px-12"
          style={{ background: 'var(--color-card)' }}
        >
          <div className="flex gap-1 overflow-x-auto">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className="flex items-center gap-2 px-4 py-3 text-sm font-caption font-medium border-b-2 transition-colors whitespace-nowrap"
                style={{
                  borderColor: activeTab === tab.id ? 'var(--color-primary)' : 'transparent',
                  color: activeTab === tab.id ? 'var(--color-primary)' : 'var(--color-muted-foreground)',
                }}
              >
                <Icon
                  name={tab.icon}
                  size={14}
                  color={activeTab === tab.id ? 'var(--color-primary)' : 'var(--color-muted-foreground)'}
                />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab content */}
        <div className="px-6 md:px-10 lg:px-12 py-8">
          {activeTab === 'overview'     && <OverviewTab    fund={fund} m={m} />}
          {activeTab === 'lp'          && <LPCommitmentsTab fund={fund} m={m} />}
          {activeTab === 'spvs'        && <SPVsTab         fund={fund} m={m} />}
          {activeTab === 'performance' && <PerformanceTab  fund={fund} m={m} />}
          {activeTab === 'documents'   && <DocumentsTab />}
        </div>
      </div>
    </AppLayout>
  );
}
